import React, { useState, useEffect } from "react";
import { MapPin, Truck, Store } from "lucide-react";
import { useFreteCalculator } from "../../hooks/useFreteCalculator";
import { useClientAddresses } from "../../hooks/useClientAddresses";
import AddressSelectionModal from "../modal/address/AddressSelectionModal";

const maskCEP = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
};

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");

const maskPhoneBR = (value) => {
  const d = String(value || "").replace(/\D/g, "");

  // Formata número com 9 dígitos: (99) 9 9999-9999
  const formatNum = (num) => {
    if (num.length === 0) return "";
    if (num.length <= 1) return num; // Apenas o 9
    if (num.length <= 5) return `${num.slice(0, 1)} ${num.slice(1)}`; // 9 XXXX
    return `${num.slice(0, 1)} ${num.slice(1, 5)}-${num.slice(5, 9)}`; // 9 XXXX-XXXX
  };

  // Com DDI Brasil (+55)
  if (d.startsWith("55")) {
    const rest = d.slice(2, 13); // 2 (DDD) + até 9 dígitos
    const area = rest.slice(0, 2);
    const num = rest.slice(2);
    if (!area) return "+55 ";
    if (!num) return `+55 (${area}) `;
    return `+55 (${area}) ${formatNum(num)}`;
  }

  // Sem DDI - Formato padrão: (99) 9 9999-9999
  const area = d.slice(0, 2);
  const num = d.slice(2, 11); // até 9 dígitos
  if (!area) return "";
  if (!num) return `(${area}) `;
  return `(${area}) ${formatNum(num)}`;
};

const formatBRL = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

export default function DeliverySection({
  deliveryType,
  setDeliveryType,
  customerInfo,
  setCustomerInfo,
  address,
  setAddress,
  freight,
  setFreight,
  validateDelivery,
  deliveryEnabled,
  setUiBusy,
  showMessage,
}) {
  const {
    freight: freightData,
    loading: freightLoading,
    error: freightError,
    calculate: calculateFreight,
    clearFreight,
  } = useFreteCalculator();

  const {
    addresses,
    loading: addressesLoading,
    fetchAddresses,
    clearAddresses,
  } = useClientAddresses();

  const [loadingCEP, setLoadingCEP] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [hasSearchedAddresses, setHasSearchedAddresses] = useState(false);

  // Oculta "Delivery" só quando a loja confirmou que está indisponível
  // (null = ainda não verificado)
  const showDeliveryOption = deliveryEnabled !== false;

  // Handler para mudar tipo de entrega com validação
  const handleDeliveryClick = async () => {
    if (validateDelivery) {
      const isValid = await validateDelivery();
      if (isValid) {
        setDeliveryType("delivery");
      }
    } else {
      setDeliveryType("delivery");
    }
  };

  const cepDigits = onlyDigits(address.cep);

  // Atualiza freight do parent quando freightData mudar
  // (ignora resposta atrasada de um CEP que o cliente já trocou)
  useEffect(() => {
    if (freightData && freightData.requestedCep === cepDigits) {
      setFreight(freightData);
    }
  }, [freightData, cepDigits, setFreight]);

  // Frete pertence a um CEP: se o CEP mudar, o valor anterior deixa de valer
  useEffect(() => {
    if (freight?.requestedCep && freight.requestedCep !== cepDigits) {
      clearFreight();
      setFreight(null);
    }
  }, [cepDigits, freight?.requestedCep, clearFreight, setFreight]);

  // Calcula o frete sozinho quando há CEP completo e ainda não há frete para ele.
  // Cobre: dados restaurados do localStorage, volta de Retirada para Delivery e
  // CEP digitado sem clicar em "Calcular Frete" (antes o botão Finalizar ficava travado).
  useEffect(() => {
    if (deliveryType !== "delivery" || cepDigits.length !== 8) return;
    if (freight?.requestedCep === cepDigits) return;

    const timer = setTimeout(() => {
      calculateFreight(cepDigits);
    }, 500);
    return () => clearTimeout(timer);
  }, [deliveryType, cepDigits, freight?.requestedCep, calculateFreight]);

  // Limpa frete quando mudar para retirada
  useEffect(() => {
    if (deliveryType !== "delivery") {
      clearFreight();
      setFreight(null);
      clearAddresses();
      setHasSearchedAddresses(false);
    }
  }, [deliveryType, clearFreight, setFreight, clearAddresses]);

  // Função para buscar endereços do cliente
  const handleSearchAddresses = async (phone) => {
    const phoneDigits = phone || onlyDigits(customerInfo.phone);

    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      return;
    }

    // Ativa loading profissional
    if (setUiBusy) {
      setUiBusy(true);
    }

    try {
      console.log("🔍 Buscando endereços para:", phoneDigits);
      const addressList = await fetchAddresses(phoneDigits);

      // Marca que já buscou para não buscar novamente
      setHasSearchedAddresses(true);

      // Verifica se retornou dados válidos (não null por erro)
      if (addressList !== null) {
        if (addressList.length > 0) {
          // Abre modal com lista de endereços
          console.log("✅ Endereços encontrados:", addressList.length);
          setIsAddressModalOpen(true);
        } else {
          // Array vazio = não há endereços cadastrados
          console.log("📭 Nenhum endereço cadastrado");
          if (showMessage) {
            showMessage(
              "Endereços não encontrados",
              "Não encontramos endereços cadastrados para este telefone. Por favor, preencha manualmente.",
              "info"
            );
          }
        }
      } else {
        // null = erro na requisição
        console.log("❌ Erro ao buscar endereços");
      }
    } catch (error) {
      console.error("Erro ao buscar endereços:", error);
    } finally {
      if (setUiBusy) {
        setUiBusy(false);
      }
    }
  };

  // Função para selecionar um endereço do modal
  const handleSelectAddress = async (selectedAddress) => {
    console.log("✅ Endereço selecionado:", selectedAddress);

    // Preenche os campos com o endereço selecionado
    setAddress({
      cep: selectedAddress.cep || "",
      logradouro: selectedAddress.logradouro || "",
      numero: selectedAddress.number || "", // API retorna 'number'
      complemento: selectedAddress.complemento || "",
      bairro: selectedAddress.bairro || "",
      uf: selectedAddress.uf || "",
      estado: selectedAddress.estado || "",
    });

    // Fecha o modal
    setIsAddressModalOpen(false);

    // Ativa loading e calcula frete automaticamente
    if (setUiBusy) {
      setUiBusy(true);
    }

    try {
      const cepDigits = onlyDigits(selectedAddress.cep);
      const freightResult = await calculateFreight(cepDigits);

      if (freightResult) {
        if (freightResult.available && showMessage) {
          showMessage(
            "Frete Calculado",
            `${
              freightResult.message
            }\nDistância: ${freightResult.distanceKm.toFixed(
              2
            )} km\nValor: R$ ${freightResult.value.toFixed(2)}`,
            "success"
          );
        } else if (!freightResult.available && showMessage) {
          showMessage("Frete Indisponível", freightResult.message, "warning");
        }
      }
    } catch (error) {
      console.error("Erro ao calcular frete:", error);
      if (showMessage) {
        showMessage(
          "Erro ao Calcular Frete",
          "Não foi possível calcular o frete. Tente novamente.",
          "error"
        );
      }
    } finally {
      if (setUiBusy) {
        setUiBusy(false);
      }
    }
  };

  // Busca CEP na API ViaCEP e calcula frete
  const handleBuscarCEP = async () => {
    const cepDigits = onlyDigits(address.cep);

    if (cepDigits.length !== 8) {
      if (showMessage) {
        showMessage(
          "CEP Inválido",
          "Por favor, informe um CEP válido com 8 dígitos.",
          "warning"
        );
      }
      return;
    }

    // Ativa loading profissional
    if (setUiBusy) {
      setUiBusy(true);
    } else {
      setLoadingCEP(true);
    }

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepDigits}/json/`
      );

      if (!response.ok) {
        throw new Error("Erro ao consultar CEP");
      }

      const data = await response.json();

      if (data.erro) {
        if (showMessage) {
          showMessage(
            "CEP não encontrado",
            "CEP não encontrado. Verifique e tente novamente.",
            "warning"
          );
        }
        setAddress((prev) => ({
          ...prev,
          logradouro: "",
          bairro: "",
          uf: "",
          estado: "",
        }));
        return;
      }

      // Mapa de UFs para nomes de estados
      const UF_NAMES = {
        AC: "Acre",
        AL: "Alagoas",
        AP: "Amapá",
        AM: "Amazonas",
        BA: "Bahia",
        CE: "Ceará",
        DF: "Distrito Federal",
        ES: "Espírito Santo",
        GO: "Goiás",
        MA: "Maranhão",
        MT: "Mato Grosso",
        MS: "Mato Grosso do Sul",
        MG: "Minas Gerais",
        PA: "Pará",
        PB: "Paraíba",
        PR: "Paraná",
        PE: "Pernambuco",
        PI: "Piauí",
        RJ: "Rio de Janeiro",
        RN: "Rio Grande do Norte",
        RS: "Rio Grande do Sul",
        RO: "Rondônia",
        RR: "Roraima",
        SC: "Santa Catarina",
        SP: "São Paulo",
        SE: "Sergipe",
        TO: "Tocantins",
      };

      const uf = data.uf || "";
      setAddress((prev) => ({
        ...prev,
        logradouro: data.logradouro || "",
        bairro: data.bairro || "",
        uf,
        localidade: data.localidade || "",
        estado: UF_NAMES[uf] || data.localidade || "",
      }));

      // Após buscar CEP com sucesso, calcula frete automaticamente
      if (deliveryType === "delivery") {
        try {
          const freightResult = await calculateFreight(cepDigits);
          if (freightResult) {
            if (freightResult.available && showMessage) {
              showMessage(
                "Frete Calculado",
                `${
                  freightResult.message
                }\nDistância: ${freightResult.distanceKm.toFixed(
                  2
                )} km\nValor: R$ ${freightResult.value.toFixed(2)}`,
                "success"
              );
            } else if (!freightResult.available && showMessage) {
              showMessage(
                "Frete Indisponível",
                freightResult.message,
                "warning"
              );
            }
          }
        } catch (freightError) {
          console.error("Erro ao calcular frete:", freightError);
          if (showMessage) {
            showMessage(
              "Erro ao Calcular Frete",
              "Não foi possível calcular o frete. Tente novamente.",
              "error"
            );
          }
        }
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
      if (showMessage) {
        showMessage(
          "Erro ao Buscar CEP",
          "Não foi possível consultar o CEP agora. Tente novamente.",
          "error"
        );
      }
    } finally {
      // Desativa loading profissional
      if (setUiBusy) {
        setUiBusy(false);
      } else {
        setLoadingCEP(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 space-y-4 sm:space-y-6">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
        <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-brand-primary" />
        Informações de Entrega
      </h2>

      {/* Tipo de Entrega */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Como deseja receber?
        </label>
        <div
          className={`grid grid-cols-1 gap-3 ${
            showDeliveryOption ? "sm:grid-cols-2" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => setDeliveryType("pickup")}
            className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition ${
              deliveryType === "pickup"
                ? "border-brand-primary bg-red-50 text-brand-primary"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <Store className="w-5 h-5" />
            <span className="font-medium">Retirada</span>
          </button>

          {showDeliveryOption && (
            <button
              type="button"
              onClick={handleDeliveryClick}
              className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition ${
                deliveryType === "delivery"
                  ? "border-brand-primary bg-red-50 text-brand-primary"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <Truck className="w-5 h-5" />
              <span className="font-medium">Delivery</span>
            </button>
          )}
        </div>
      </div>

      {/* Dados do Cliente */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome Completo *
          </label>
          <input
            type="text"
            value={customerInfo.name}
            onChange={(e) =>
              setCustomerInfo({ ...customerInfo, name: e.target.value })
            }
            placeholder="Seu nome completo"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Telefone/WhatsApp *
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="tel"
              value={maskPhoneBR(customerInfo.phone)}
              onChange={(e) => {
                const digits = onlyDigits(e.target.value);
                setCustomerInfo({ ...customerInfo, phone: digits });
                // Reseta flag quando mudar telefone
                if (digits.length < 10) {
                  setHasSearchedAddresses(false);
                }
              }}
              onBlur={() => {
                // Busca endereços apenas quando sair do campo
                if (deliveryType === "delivery" && !hasSearchedAddresses) {
                  const phoneDigits = onlyDigits(customerInfo.phone);
                  if (phoneDigits.length === 10 || phoneDigits.length === 11) {
                    handleSearchAddresses(phoneDigits);
                  }
                }
              }}
              placeholder="(99) 9 9999-9999"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
            {deliveryType === "delivery" && hasSearchedAddresses && (
              <button
                type="button"
                onClick={() => {
                  setHasSearchedAddresses(false);
                  const phoneDigits = onlyDigits(customerInfo.phone);
                  if (phoneDigits.length >= 10) {
                    handleSearchAddresses(phoneDigits);
                  }
                }}
                className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm hover:bg-gray-200 transition whitespace-nowrap"
                title="Buscar endereços novamente"
              >
                🔍 Buscar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Endereço (apenas para delivery) */}
      {deliveryType === "delivery" && (
        <div className="space-y-4 pt-4 border-t">
          <h3 className="font-semibold text-gray-800">Endereço de Entrega</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CEP *
              </label>
              <input
                type="text"
                value={maskCEP(address.cep)}
                onChange={(e) =>
                  setAddress({ ...address, cep: onlyDigits(e.target.value) })
                }
                placeholder="00000-000"
                maxLength={9}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
              <button
                type="button"
                onClick={handleBuscarCEP}
                disabled={loadingCEP || freightLoading}
                className="w-full px-4 py-2 rounded-lg bg-gray-900 text-white text-sm hover:bg-gray-800 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Truck className="w-4 h-4" />
                {loadingCEP || freightLoading
                  ? "Calculando..."
                  : "Calcular Frete"}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número *
              </label>
              <input
                type="text"
                value={address.numero}
                onChange={(e) =>
                  setAddress({ ...address, numero: e.target.value })
                }
                placeholder="Número"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Logradouro *
            </label>
            <input
              type="text"
              value={address.logradouro}
              onChange={(e) =>
                setAddress({ ...address, logradouro: e.target.value })
              }
              placeholder="Rua, Avenida, etc"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              disabled={loadingCEP}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bairro *
              </label>
              <input
                type="text"
                value={address.bairro}
                onChange={(e) =>
                  setAddress({ ...address, bairro: e.target.value })
                }
                placeholder="Bairro"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                disabled={loadingCEP}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Complemento
              </label>
              <input
                type="text"
                value={address.complemento}
                onChange={(e) =>
                  setAddress({ ...address, complemento: e.target.value })
                }
                placeholder="Apto, Bloco, etc"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Mostrar loading do frete */}
          {freightLoading && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="text-sm font-medium text-blue-800">
                  Calculando frete...
                </p>
              </div>
            </div>
          )}

          {/* Mostrar valor do frete */}
          {!freightLoading && freight && freight.available && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">Frete Disponível</p>
                  <p className="text-sm text-green-600">
                    Distância: {freight.distanceKm?.toFixed(2)} km
                  </p>
                </div>
                <p className="text-xl font-bold text-green-700">
                  {formatBRL(freight.value)}
                </p>
              </div>
            </div>
          )}

          {/* Falha ao calcular (rede/servidor): o cliente pode tentar de novo pelo botão */}
          {!freightLoading && !freight && freightError && cepDigits.length === 8 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-800">
                Não foi possível calcular o frete agora. Toque em "Calcular Frete"
                para tentar novamente.
              </p>
            </div>
          )}

          {!freightLoading && freight && !freight.available && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-800">
                {freight.message || "Frete indisponível para este endereço"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Seleção de Endereço */}
      <AddressSelectionModal
        isOpen={isAddressModalOpen}
        addresses={addresses}
        onSelect={handleSelectAddress}
        onClose={() => setIsAddressModalOpen(false)}
      />
    </div>
  );
}
